import { useTranslations } from "@zapier/i18n";

import {
  Button,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Text,
} from "@zapier/design-system";

import { useNotification } from "@zapier/utils";
import {
  type ExportZapsResponse,
  getMessageFromNetworkError,
  useExportZaps,
  Zap,
} from "@zapier/client";
import { downloadFile } from "../../../utils/downloadFile";

function getExportedZapsCount(response: ExportZapsResponse): number {
  return response?.zaps?.length || 0;
}

interface Props {
  zaps: Zap[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSettled?: () => void;
}

interface ContentProps extends Props {
  onSubmit: () => void;
}

function ZapExportModalContent({ zaps, setIsOpen, onSubmit }: ContentProps) {
  const t = useTranslations("zap-export-modal");

  return (
    <ModalDialogRoot isOpen={true} onOpenChange={setIsOpen}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the form deliberately stops click/keydown propagation to keep the modal open and submit on Enter */}
      <form
        onSubmit={onSubmit}
        onClick={(event) => event.stopPropagation()} // prevent the modal from closing when clicking inside the form
        onKeyDown={
          // if the enter key is being pressed submit the form
          (event) => {
            event.stopPropagation();
            return event.key === "Enter" && onSubmit();
          }
        }
      >
        <ModalDialogHeader textAlign="start">
          {t("headings.export-title", {
            count: zaps.length,
            title: zaps[0].title,
          })}
        </ModalDialogHeader>
        <ModalDialogBody textAlign="start">
          <Text type="Body1" tag="p">
            {t("messages.ownership-notice")}
          </Text>
        </ModalDialogBody>
        <ModalDialogFooter>
          <Button variant="secondary" onPress={() => setIsOpen(false)}>
            {t("buttons.cancel")}
          </Button>
          <Button type="submit" onPress={onSubmit}>
            {t("buttons.export")}
          </Button>
        </ModalDialogFooter>
      </form>
    </ModalDialogRoot>
  );
}

export function ZapExportModal(props: Props) {
  const { zaps, isOpen, setIsOpen, onSettled } = props;
  const { createNotification } = useNotification();
  const { exportZaps } = useExportZaps();
  const t = useTranslations("zap-export-modal");

  /** This callback needs to happen outside of the modal so that it can
   * still display a notification when the request completes after the modal has closed.
   */
  const submit = () => {
    exportZaps(
      {
        zapIds: zaps.map((zap) => zap.id),
      },
      {
        onSuccess: (response) => {
          const exportedZapsCount = getExportedZapsCount(response);
          const selectedZapsCount = zaps?.length;
          const shouldShowNoOwnedZapsExportedMessage = exportedZapsCount === 0;
          const shouldShowPartialOwnedZapsExportedMessage =
            exportedZapsCount > 0 && exportedZapsCount < selectedZapsCount;

          createNotification({
            message: shouldShowNoOwnedZapsExportedMessage
              ? t("notifications.export-none-owned")
              : shouldShowPartialOwnedZapsExportedMessage
                ? t("notifications.export-partial-owned")
                : t("notifications.export-complete"),
            type: shouldShowNoOwnedZapsExportedMessage ? "error" : "success",
          });

          if (!shouldShowNoOwnedZapsExportedMessage) {
            // Start the file download
            const now = new Date().toISOString();
            downloadFile(`exported-zap-${now}.json`, JSON.stringify(response));
          }
        },
        onError: (error) => {
          createNotification({
            title: t("errors.unable-to-export", { count: zaps.length }),
            message: getMessageFromNetworkError(error),
            type: "error",
          });
        },
        onSettled: () => {
          onSettled?.();
        },
      },
    );
    setIsOpen(false);
  };

  if (!isOpen || zaps.length === 0) {
    return;
  }

  return <ZapExportModalContent {...props} onSubmit={submit} />;
}
